export type Track = (event: string, props: Record<string, unknown>) => void;

/** Coarse platform facts sent with every client event (docs/metrics.md): four short strings. */
export interface Platform {
  os: "ios" | "android" | "windows" | "macos" | "chromeos" | "linux" | "other";
  browser: "safari" | "chrome" | "edge" | "firefox" | "samsung" | "other";
  device_class: "phone" | "tablet" | "laptop";
  input: "keyboard" | "touch";
}

/**
 * The platform from the user agent and the primary pointer. iPadOS Safari reports itself as a Mac,
 * so a "Mac" with a touch screen is an iPad. A coarse primary pointer means touch: a phone when the
 * screen's short side is under 600 CSS px, otherwise a tablet. Touch laptops keep a fine pointer.
 */
export function platformOf(
  ua: string,
  maxTouchPoints: number,
  coarse: boolean,
  shortSide: number,
): Platform {
  const ipad = /Macintosh/.test(ua) && maxTouchPoints > 1;
  const os: Platform["os"] =
    /iPhone|iPad|iPod/.test(ua) || ipad
      ? "ios"
      : /Android/.test(ua)
        ? "android"
        : /Windows/.test(ua)
          ? "windows"
          : /CrOS/.test(ua)
            ? "chromeos"
            : /Macintosh|Mac OS X/.test(ua)
              ? "macos"
              : /Linux/.test(ua)
                ? "linux"
                : "other";
  const browser: Platform["browser"] = /\bEdg/.test(ua)
    ? "edge"
    : /SamsungBrowser/.test(ua)
      ? "samsung"
      : /Firefox|FxiOS/.test(ua)
        ? "firefox"
        : /CriOS|Chrome\//.test(ua)
          ? "chrome"
          : /Safari\//.test(ua)
            ? "safari"
            : "other";
  const touch = coarse || ipad;
  return {
    os,
    browser,
    device_class: !touch ? "laptop" : shortSide < 600 ? "phone" : "tablet",
    input: touch ? "touch" : "keyboard",
  };
}

/**
 * Where client events go, or null to keep them on the page: this origin's `/api/events`, which the
 * same-origin router hands to the api (ADR 0010). Only Obean's own addresses send: production and
 * staging, all under obean.com since 2026-10-25 (no workers.dev anywhere). Local dev sends nothing.
 */
const SENDING = new Set(["obean.com", "www.obean.com", "staging.obean.com"]);

export function eventsUrl(hostname: string): string | null {
  return SENDING.has(hostname) ? "/api/events" : null;
}

/** A UUIDv7 (time-ordered; docs/metrics.md asks for v7 event ids). */
export function uuidv7(
  now = Date.now(),
  random: Uint8Array = crypto.getRandomValues(new Uint8Array(10)),
): string {
  const b = new Uint8Array(16);
  let t = now;
  for (let i = 5; i >= 0; i--) {
    b[i] = t % 256;
    t = Math.floor(t / 256);
  }
  b[6] = 0x70 | ((random[0] ?? 0) & 0x0f); // version 7
  b[7] = random[1] ?? 0;
  b[8] = 0x80 | ((random[2] ?? 0) & 0x3f); // RFC 9562 variant
  b.set(random.subarray(3, 10), 9);
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** app_opened's `referrer_domain`, bucketed (docs/metrics.md): no referrer, this site, or elsewhere. */
export function referrerBucket(referrer: string, host: string): "none" | "same_site" | "other" {
  if (!referrer) return "none";
  try {
    return new URL(referrer).host === host ? "same_site" : "other";
  } catch {
    return "other";
  }
}

/**
 * app_opened's `entry`: a room link with an invite secret, a plain room link, solo play (ADR 0020),
 * the start reached from solo's "Play with a friend" (`?from=solo`), or the start.
 */
export function entryOf(
  pathname: string,
  hash: string,
  search = "",
): "invite" | "room_url" | "solo" | "from_solo" | "landing" {
  if (pathname === "/solo") return "solo";
  if (!pathname.startsWith("/r/"))
    return new URLSearchParams(search).get("from") === "solo" ? "from_solo" : "landing";
  return hash.length > 1 ? "invite" : "room_url";
}

type Scalar = string | number | boolean | null;

/** Props as the api accepts them: flat scalars only (anything else is dropped, never stringified). */
export function flatProps(props: Record<string, unknown>): Record<string, Scalar> {
  const out: Record<string, Scalar> = {};
  for (const [key, value] of Object.entries(props))
    if (
      value === null ||
      typeof value === "boolean" ||
      typeof value === "string" ||
      (typeof value === "number" && Number.isFinite(value))
    )
      out[key] = value;
  return out;
}

/** The api's limit is 50 events per batch; sending at 20 keeps batches small. */
const BATCH = 20;
/**
 * Whatever is queued goes every 30 s (and when the tab is hidden or the page goes): half the
 * requests of every 15 s, which doubles how many players D1 takes before it overloads (the
 * capacity test, 2026-10-24: ~165 batches a second). Nothing extra is lost: hiding or closing the
 * page still sends what's waiting.
 */
const FLUSH_MS = 30_000;

/** Batches events and hands each batch to `send` as a JSON body. */
export class EventQueue {
  readonly #send: (body: string) => void;
  #events: object[] = [];

  constructor(send: (body: string) => void) {
    this.#send = send;
  }

  add(event: object): void {
    this.#events.push(event);
    if (this.#events.length >= BATCH) this.flush();
  }

  flush(): void {
    while (this.#events.length > 0)
      this.#send(JSON.stringify({ events: this.#events.splice(0, BATCH) }));
  }
}

/** A beacon still goes out while the page unloads. The answer is never read. */
const beacon = (url: string) => (body: string) => {
  if (navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
  fetch(url, {
    method: "POST",
    body,
    keepalive: true,
    headers: { "content-type": "application/json" },
  }).catch(() => undefined);
};

function startQueue(): EventQueue | null {
  const url = eventsUrl(location.hostname);
  if (!url) return null;
  const queue = new EventQueue(beacon(url));
  setInterval(() => {
    queue.flush();
  }, FLUSH_MS);
  // The last chance to send: the tab is hidden (it may never come back) or the page goes away.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") queue.flush();
  });
  window.addEventListener("pagehide", () => {
    queue.flush();
  });
  return queue;
}

/** Set up on the first event (nothing runs at import). */
let session: {
  queue: EventQueue | null;
  /** Random per tab load, kept in memory only (no cookie, no storage). */
  tabSessionId: string;
  platform: Platform | null;
} | null = null;

/**
 * Records a client event (docs/metrics.md). It always goes out as an `obean:telemetry` DOM event
 * (the e2e tests listen), and on preview and staging it is also queued for POST /api/events, with
 * `match_id` and `room_id` lifted from the props into the envelope.
 */
export const track: Track = (event, props) => {
  window.dispatchEvent(new CustomEvent("obean:telemetry", { detail: { event, props } }));
  session ??= { queue: startQueue(), tabSessionId: uuidv7(), platform: null };
  if (!session.queue) return;
  session.platform ??= platformOf(
    navigator.userAgent,
    navigator.maxTouchPoints,
    matchMedia("(pointer: coarse)").matches,
    Math.min(screen.width, screen.height),
  );
  const { match_id: matchId, room_id: roomId, ...rest } = props;
  session.queue.add({
    event_id: uuidv7(),
    event,
    schema_version: 1,
    ts_client: Date.now(),
    tab_session_id: session.tabSessionId,
    ...(typeof matchId === "string" ? { match_id: matchId } : {}),
    ...(typeof roomId === "string" ? { room_id: roomId } : {}),
    platform: session.platform,
    props: flatProps(rest),
  });
};
